import React from 'react';
import { Platform, Text, StyleSheet } from 'react-native';
import AdminModal from '../../components/AdminModal';
import AdminButton from '../../components/AdminButton';
import { AdminColors } from '../../constants/adminColors';
import { useTheme } from '../../../../theme/useTheme';

interface DeleteConfirmModalProps {
  visible: boolean;
  /** A short label or preview of the item being deleted (e.g. banner message, event name). */
  itemLabel: string;
  /** Override the confirmation body text. Defaults to the generic "Are you sure…" message. */
  message?: string;
  /** Show loading state on the delete button. */
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  visible,
  itemLabel,
  message,
  loading,
  onClose,
  onConfirm,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  return (
    <AdminModal
      visible={visible}
      title="Confirm Delete"
      onClose={onClose}
      footer={
        <>
          <AdminButton label="Cancel" variant="secondary" onPress={onClose} disabled={loading} />
          <AdminButton label="Delete" variant="danger" onPress={onConfirm} loading={loading} />
        </>
      }
    >
      <Text
        style={[
          styles.preview,
          isDark && {
            color: colors.text.secondary,
            backgroundColor: colors.background.elevated,
            borderLeftColor: colors.brand.primary,
          },
        ]}
        numberOfLines={2}
      >
        {itemLabel}
      </Text>
      <Text style={[styles.body, isDark && { color: colors.text.primary }]}>
        {message ?? 'Are you sure you want to delete this item? This cannot be undone.'}
      </Text>
    </AdminModal>
  );
};

const styles = StyleSheet.create({
  preview: {
    fontSize: 13,
    color: AdminColors.textSecondary,
    backgroundColor: AdminColors.bgPage,
    borderLeftWidth: 3,
    borderLeftColor: AdminColors.blue700,
    padding: 12,
    borderRadius: 4,
    marginBottom: 16,
  },
  body: {
    fontSize: 14,
    color: AdminColors.textPrimary,
    lineHeight: 20,
  },
});

export default DeleteConfirmModal;
